import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PeopleIcon from "@mui/icons-material/People";
import PersonIcon from "@mui/icons-material/Person";
import PublicIcon from "@mui/icons-material/Public";
import { Accordion, AccordionDetails, AccordionSummary, AutocompleteChangeReason, ListItemButton, ListItemButtonProps, ListItemText } from "@mui/material";
import { Box, DeleteDialog, FlexColumn, FlexRow, OverflowText, SingleSelect } from "designSystem/index";
import { useLocale } from "designSystem/locale";
import { ComponentItem } from "designSystem/types/component-item-types";
import { observer } from "mobx-react-lite";
import React, { FC, useRef } from "react";
import { useCustomViewDialogStore } from "../CustomViewDialogStore";
import CustomViewFavoriteButton from "../CustomViewFavoriteButton";
import css from "./CustomViewDialogLeftPanelAccordion.module.css";
import { CustomViewItem } from "./CustomViewItem";
import CustomViewItemActionMenu from "./CustomViewItemActionMenu";
import CustomViewItemActionMenuPartial from "./CustomViewItemActionMenuPartial";
import DefaultViewPopup from "./DefaultViewPopup";
import { NonGlobalCustomView } from "./customViewDialogLeftPanelService";

export enum ViewCategory {
	PUBLIC = "PUBLIC",
	ROLE_BASED = "ROLE_BASED",
	PERSONAL = "PERSONAL",
}

export const CategoryIconMap = {
	[ViewCategory.PUBLIC]: <PublicIcon />,
	[ViewCategory.ROLE_BASED]: <PeopleIcon />,
	[ViewCategory.PERSONAL]: <PersonIcon />,
};

export interface AccordionSummaryCommonProps {
	/** view category */
	category: ViewCategory;

	/** group item, either role based or personal */
	customViewItem?: NonGlobalCustomView;

	/** label for this accordion */
	label: string;
}

const AccordionSummaryCommon: FC<AccordionSummaryCommonProps> = observer((props: AccordionSummaryCommonProps) => {
	const { category, customViewItem, label } = props;
	const isPersonal = category === ViewCategory.PERSONAL;

	return (
		<AccordionSummary className={css.accordionSummary} expandIcon={<ExpandMoreIcon />}>
			<FlexRow minWidth={0} width={1}>
				<FlexColumn justifyContent="center">
					{CategoryIconMap[category]}
				</FlexColumn>
				<FlexColumn
					className={css.labelName}
					flexGrow={1}
					justifyContent="center"
					pl={2}
				>
					<OverflowText
						bold
						color="grey.A100"
						text={label}
					/>
				</FlexColumn>
				<FlexColumn flexShrink={0} pr={6}>
					{!isPersonal && <DefaultViewPopup customViewItem={customViewItem} />}
				</FlexColumn>
			</FlexRow>
		</AccordionSummary>
	);
});

interface AccordionItemProps extends ListItemButtonProps {
	/** custom view item */
	customViewItem: CustomViewItem;
}

const AccordionItem: FC<AccordionItemProps> = observer((props: AccordionItemProps) => {
	const { customViewItem, ...otherProps } = props;
	const {
		cancelDeleting,
		continueDeleting,
		isDeleting,
		name,
		selected,
	} = customViewItem;

	const hoverRef = useRef(null);
	const loc = useLocale();
	const typeName = loc.type("_CS_PreferenceView");

	const { hasPersonalAccess, isSaving } = useCustomViewDialogStore();

	return (
		<ListItemButton
			className={css.list}
			data-csi-automation="AccordionItem"
			disabled={isSaving}
			ref={hoverRef}
			selected={selected}
			{...otherProps}
		>
			<ListItemText primary={name} sx={{ flexGrow: "0" }} />
			<CustomViewFavoriteButton
				className={css.favoriteButton}
				customViewItem={customViewItem}
				design="defaultOnGrey"
				showOnHover
			/>
			<Box flexGrow={1} />
			{hasPersonalAccess ? (
				<CustomViewItemActionMenu customViewItem={customViewItem} {...{ ref: hoverRef }} />
			) : (
				<CustomViewItemActionMenuPartial customViewItem={customViewItem} {...{ ref: hoverRef }} />
			)}
			<DeleteDialog
				onCancel={cancelDeleting}
				onConfirm={continueDeleting}
				open={isDeleting}
				typeName={typeName}
			/>
		</ListItemButton>
	);
});

export const ViewAccordionForPublic: FC<{}> = observer(() => {
	const loc = useLocale();
	const label = loc.resource("CustomViewCategoryGlobal");

	const {
		customViewStore: { filteredSystemWideItems },
	} = useCustomViewDialogStore();

	return (
		<FlexRow>
			<Accordion
				className={css.accordion}
				data-csi-automation="ViewAccordionForPublic"
				defaultExpanded
			>
				<AccordionSummaryCommon
					category={ViewCategory.PUBLIC}
					label={label}
				/>
				<AccordionDetails sx={{ pl: 5 }}>
					{filteredSystemWideItems.map((item) => (
						<AccordionItem
							className={css.list}
							customViewItem={item}
							data-csi-custom-view={String(item.url)}
							key={item.url}
							onClick={item.select}
						/>
					))
					}
				</AccordionDetails>
			</Accordion>
		</FlexRow>
	);
});

interface ViewAccordionForNonPublicProps {
	/** the category */
	category: ViewCategory;

	/** current selected user to show the views for */
	currentSelectedUserItem?: ComponentItem;

	/** indicate if it's personal view */
	isPersonal: boolean;

	/** the item of non global custom view, which is role based and personal custom view */
	nonGlobalCustomViewItem: NonGlobalCustomView;

	/** handler for switching user */
	onSwitchUser?: (event: React.ChangeEvent<{}>, value: ComponentItem, reason: AutocompleteChangeReason) => void;

	/** indicate if show the user selector */
	showUserSelector?: boolean;

	/** user list from which the admin can switch */
	userList?: ComponentItem[];
}

export const ViewAccordionForNonPublic: FC<ViewAccordionForNonPublicProps> = observer((props: ViewAccordionForNonPublicProps) => {
	const {
		category,
		currentSelectedUserItem,
		isPersonal,
		nonGlobalCustomViewItem,
		onSwitchUser,
		showUserSelector,
		userList,
	} = props;

	const { name, filteredViewItems } = nonGlobalCustomViewItem || {};
	const loc = useLocale();
	const label = isPersonal ? loc.resource("CustomViewCategoryPersonal") : name;

	return (
		<Accordion
			className={css.accordion}
			data-csi-automation="ViewAccordionForNonPublic"
			defaultExpanded
		>
			<AccordionSummaryCommon
				category={category}
				customViewItem={nonGlobalCustomViewItem}
				label={label}
			/>
			<AccordionDetails sx={{ pl: 5 }}>
				{showUserSelector && (
					<Box pb={2} pt={1}>
						<SingleSelect
							allowPinyin
							onChange={onSwitchUser}
							options={userList}
							required
							value={currentSelectedUserItem}
						/>
					</Box>
				)}
				{filteredViewItems?.map((item) => (
					<AccordionItem
						className={css.list}
						customViewItem={item}
						data-csi-custom-view={String(item.url)}
						key={item.url}
						onClick={item.select}
					/>
				))
				}
			</AccordionDetails>
		</Accordion>
	);
});